import { useEffect, useId, useRef, useState } from 'react';
import { CompanyRequestError } from './api';
import {
  fetchProcessingSettings,
  saveProcessingSettings,
  type ProcessingSettings,
} from './processing-api';
import { useCompany } from './context';
import { fetchTeam } from './team-api';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import companyProcessingCopy from '@/i18n/ns/company-processing';
import {
  PROCESSING_MODEL_LABELS,
  AUTO_FALLBACK_MODEL_LABEL,
} from '../workspace/model-choices.generated';
import { WorkspaceIcon } from '../workspace/icons';
import { Callout } from '@/components/ui/content';
import { StatusPill } from '@/components/ui/kit';
import { voiceConfigStore } from '@/voice/state';

type Mode = ProcessingSettings['mode'];

type ProcessingCopy = Record<string, string>;

/** What is true now: the saved setting, never the unsaved draft. */
function NowCallout({ mode, copy }: { mode: Mode; copy: ProcessingCopy }) {
  const eu = mode === 'eu';
  return (
    <Callout
      tone="info"
      icon={<WorkspaceIcon name={eu ? 'shield' : 'globe'} size={18} />}
      title={eu ? copy.nowEuTitle : copy.nowGlobalTitle}
    >
      <p>{eu ? copy.nowEuBody : copy.nowGlobalBody}</p>
      <p className="company-processing-scope">{copy.nowScope}</p>
    </Callout>
  );
}

function WhatChanges({ copy }: { copy: ProcessingCopy }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const lines = [
    formatCopy(copy.detailsModel, {
      model: PROCESSING_MODEL_LABELS.eu,
      fallback: AUTO_FALLBACK_MODEL_LABEL,
    }),
    copy.detailsVoice,
    copy.detailsMeetings,
    copy.detailsCompaction,
    copy.detailsOthers,
    copy.detailsNext,
  ];
  return (
    <div className="company-processing-details">
      <button
        type="button"
        className="company-processing-disclosure"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
      >
        {copy.detailsToggle}
        <WorkspaceIcon
          name="down"
          size={16}
          style={{ transform: open ? 'rotate(180deg)' : undefined }}
        />
      </button>
      <ul id={id} hidden={!open}>
        {lines.map((line, index) => (
          <li key={`${index}-${line}`}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

export function CompanyProcessingSettings({
  onEditStateChange,
}: {
  onEditStateChange: (state: { dirty: boolean; busy: boolean }) => void;
}) {
  const copy = useNamespaceCopy(companyProcessingCopy);
  const OPTIONS = [
    { mode: 'eu' as const, title: copy.euTitle, body: copy.euBody },
    { mode: 'global' as const, title: copy.globalTitle, body: copy.globalBody },
  ];
  const { data } = useCompany();
  const [settings, setSettings] = useState<ProcessingSettings | null>(null);
  const [draft, setDraft] = useState<Mode>('eu');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A calm "here's the latest" (a lost save race), not a failure.
  const [notice, setNotice] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const owner = ['owner', 'admin'].includes(data?.membership?.role ?? '');
  // Members see whom to ask: an Owner's display name from the team they can read.
  const [ownerName, setOwnerName] = useState<string | null>(null);
  useEffect(() => {
    if (owner || !data?.company?.id) return;
    let live = true;
    void fetchTeam()
      .then((team) => {
        const name = team.people.find((person) => person.role === 'owner')?.displayName ?? null;
        if (live) setOwnerName(name);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [owner, data?.company?.id]);
  const company = data?.company?.name ?? '';
  const generation = useRef(0);
  /** Resolves to this load's generation when it showed fresh settings, else null. */
  const load = async (): Promise<number | null> => {
    const companyId = data?.company?.id;
    const currentGeneration = ++generation.current;
    setSettings(null);
    setDraft('eu');
    setBusy(false);
    setError(null);
    setNotice(null);
    setSaved(false);
    try {
      if (!companyId) return null;
      const result = await fetchProcessingSettings(companyId);
      if (generation.current !== currentGeneration || result.settings.organizationId !== companyId)
        throw new Error('company_changed_reload');
      setSettings(result.settings);
      setDraft(result.settings.mode);
      return currentGeneration;
    } catch {
      if (generation.current === currentGeneration) setError(copy.loadError);
      return null;
    }
  };
  useEffect(() => void load(), [data?.company?.id]);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  const dirty = Boolean(settings && draft !== settings.mode);
  useEffect(() => {
    onEditStateChange({ dirty, busy });
  }, [dirty, busy, onEditStateChange]);
  const save = async () => {
    if (!settings) return;
    const companyId = data?.company?.id;
    const currentGeneration = generation.current;
    const revision = settings.revision;
    setBusy(true);
    setError(null);
    setNotice(null);
    setSaved(false);
    try {
      const result = await saveProcessingSettings(settings.organizationId, {
        mode: draft,
        revision,
      });
      if (
        generation.current !== currentGeneration ||
        result.settings.organizationId !== companyId ||
        result.settings.revision <= revision
      )
        return;
      setSettings(result.settings);
      setDraft(result.settings.mode);
      setSaved(true);
      void voiceConfigStore.refresh(
        `${data?.membership?.userId ?? ''}:${result.settings.organizationId}`,
      );
    } catch (cause) {
      if (generation.current !== currentGeneration) return;
      if (cause instanceof CompanyRequestError && cause.status === 409) {
        // Someone else saved first: show their choice, then say so.
        // Say so only when this same reload showed the other choice. A failed
        // reload keeps its own error; a company switch meanwhile owns the page.
        const reloaded = await load();
        if (reloaded !== null && generation.current === reloaded) setNotice(copy.conflict);
        return;
      }
      setError(cause instanceof CompanyRequestError ? cause.message : copy.saveError);
    } finally {
      if (generation.current === currentGeneration) setBusy(false);
    }
  };

  return (
    <>
      <h1>{copy.title}</h1>
      <p className="company-description">{formatCopy(copy.intro, { company })}</p>
      {!owner && settings && (
        <p className="company-processing-member-note">
          {ownerName
            ? formatCopy(copy.adminOnlyAsk, { owner: ownerName, company })
            : copy.adminOnly}
        </p>
      )}
      {!settings ? (
        error ? (
          <div>
            <p role="alert" className="company-error">
              {error}
            </p>
            <button type="button" className="company-secondary" onClick={() => void load()}>
              {copy.retry}
            </button>
          </div>
        ) : (
          <p role="status">{copy.loading}</p>
        )
      ) : (
        <form
          className="company-form company-processing"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <NowCallout mode={settings.mode} copy={copy} />
          {!owner ? (
            // Members read the current choice; there is nothing for them to pick.
            <div className="company-processing-option company-processing-summary">
              <span className="company-processing-option-copy">
                <span className="company-processing-summary-label">{copy.currentChoice}</span>
                <span className="company-processing-option-title">
                  {OPTIONS.find((option) => option.mode === settings.mode)?.title}
                </span>
                <span className="company-processing-option-body">
                  {OPTIONS.find((option) => option.mode === settings.mode)?.body}
                </span>
              </span>
            </div>
          ) : (
            <fieldset className="company-processing-options" disabled={busy}>
              <legend className="ws-sr-only">{copy.choicesLabel}</legend>
              {OPTIONS.map((option) => (
                <label
                  key={option.mode}
                  className="company-processing-option"
                  data-selected={draft === option.mode}
                >
                  <input
                    type="radio"
                    name="processing-mode"
                    value={option.mode}
                    checked={draft === option.mode}
                    onChange={() => {
                      setDraft(option.mode);
                      setSaved(false);
                    }}
                  />
                  <span className="company-processing-option-copy">
                    <span className="company-processing-option-title">
                      {option.title}
                      {settings.mode === option.mode && <StatusPill>{copy.current}</StatusPill>}
                    </span>
                    <span className="company-processing-option-body">{option.body}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          )}
          <WhatChanges copy={copy} />
          {(dirty || busy || saved || error || notice) && (
            <div className="company-processing-decide">
              {dirty && (
                <div className="company-processing-decision" role="status">
                  <p>
                    {formatCopy(draft === 'eu' ? copy.decisionToEu : copy.decisionToGlobal, {
                      company,
                    })}
                  </p>
                  <p className="company-processing-consequence">
                    {draft === 'eu' ? copy.consequenceToEu : copy.consequenceToGlobal}
                  </p>
                </div>
              )}
              {notice && (
                <div role="status">
                  <Callout tone="info">
                    <p>{notice}</p>
                  </Callout>
                </div>
              )}
              {error && (
                <p role="alert" className="company-error">
                  {error}
                </p>
              )}
              {/* Save appears with a change, like the decision it confirms. */}
              {owner && (dirty || busy || saved) && (
                <div className="company-form-actions">
                  <button className="company-primary" disabled={busy || !dirty}>
                    {busy ? copy.saving : copy.save}
                  </button>
                  {saved && (
                    <span role="status" className="company-processing-saved">
                      {copy.saved}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </form>
      )}
    </>
  );
}
